<script lang="ts">
  import { onMount } from "svelte"
  import { fade } from "svelte/transition"
  import printJS from "print-js"
  import { Stretch } from "svelte-loading-spinners"
  import { ChevronRight, Printer } from "lucide-svelte"
  import { monthsAbbr } from "$app/constants"
  import { xhr, toast, formatDate } from "$app/utils"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { appStore } from "$app/store.svelte"

  let date_start = $state(appStore.date_start_str)
  let date_end = $state(appStore.date_end_str)

  let dateStart = $state(null)
  let dateEnd = $state(null)
  let curr_date_start = $state(null)
  let currDateStart = $state(null)
  let notesPromise = $state(Promise.resolve())

  onMount(() => {
    dateStart = new Date(`${date_start}T00:00:00`)
    dateEnd = new Date(`${date_end}T23:59:59`)
    curr_date_start = date_start
    currDateStart = new Date(`${curr_date_start}T00:00:00`)
    notesPromise = fetchData(date_start)
  })

  let meetingNotes: MeetingNotesType = null

  let currentNote: MeetingNotesType = $state(null)
  let currentNoteText: string = $state("")

  let save_timeout = null
  let saving = $state(false)

  /**
   * Moves the calendar in weeks and fetches for activities
   * @param days Integer
   */
  async function moveWeek(days) {
    const tmpDateStart = new Date(
      dateStart.getTime() + days * 24 * 60 * 60 * 1000
    )
    const dateStartStr = tmpDateStart.toJSON().split("T")[0]

    notesPromise = fetchData(dateStartStr)
  }

  async function fetchData(date: string) {
    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/meeting_notes.json?date=${date}`
      )

      date_start = resp.data.date_start
      date_end = resp.data.date_end
      dateStart = new Date(`${date_start}T00:00:00`)
      dateEnd = new Date(`${date_end}T23:59:59`)
      meetingNotes = resp.data.meeting_notes

      currentNote = meetingNotes
      currentNoteText = currentNote.notes
    } catch (e) {
      console.log(e)
      alert("There was an error fetching the Weekly Log. Please try again.")
    }
  }

  async function saveMeetingNotes(date: string): Promise<void> {
    let data = {
      meeting_note: {
        notes: currentNote.notes,
        date: date
      }
    }

    let xhrCall = currentNote.id
      ? () => {
          return xhr.patch(
            `/plans/${appStore.plan.id}/meeting_notes/${currentNote.id}.json`,
            data
          )
        }
      : () => {
          return xhr.post(`/plans/${appStore.plan.id}/meeting_notes.json`, data)
        }

    saving = true

    try {
      const resp = await xhrCall()
      if (resp.status !== 200) throw resp

      currentNote = resp.data
    } catch (e) {
      console.log(e)
      toast(
        "There was an error saving the Weekly Log. Please try again.",
        "error"
      )
    } finally {
      saving = false
    }
  }

  function print() {
    printJS({
      printable: "print-log",
      type: "html",
      documentTitle: "Lead With Purpose",
      style: "#print-log { display: block !important; }"
    })
  }

  function saveNotes() {
    clearTimeout(save_timeout)
    save_timeout = setTimeout(() => {
      saveMeetingNotes(date_end)
    }, 600)
  }

  function updateNotes(e) {
    currentNote.notes = e.html
    saveNotes()
  }
</script>

{#await notesPromise}
  <div class="grid mt-12" style="place-items:center">
    <Stretch size="80" color="gray" />
  </div>
{:then}
  <div class="relative py-3">
    <div class="flex justify-center items-center">
      <button
        class="py-1 px-2 bg-zinc-100 text-gray-600 flex items-center gap-1 justify-center text-sm border hover:shadow hover:bg-gray-200 border-gray-300"
        onclick={() => moveWeek(-7)}
      >
        <ChevronRight class="size-4 rotate-180" />
        Previous Week
      </button>
      {#if date_start == curr_date_start}
        <div
          class="bg-indigo-600 text-zinc-100 py-2 px-6 mx-4 font-bold shadow rounded-sm"
        >
          <span class="text-sm mr-2 text-zinc-200">EDITING LOG FOR</span
          >{formatDate(dateStart)} - {formatDate(dateEnd)}
        </div>
      {:else}
        <div class="bg-blue-100 text-zinc-800 py-2 px-6 mx-4 shadow rounded-sm">
          <span class="text-sm mr-2 text-zinc-500">VIEWING LOG FOR</span
          >{formatDate(dateStart)} - {formatDate(dateEnd)}
        </div>
      {/if}

      {#if dateStart < currDateStart}
        <button
          class="py-1 px-2 bg-gray-100 text-gray-600 flex items-center gap-1 justify-center text-sm border border-gray-300"
          onclick={() => moveWeek(7)}
        >
          Next Week
          <ChevronRight class="size-4" />
        </button>
      {:else if date_start == curr_date_start}
        <button
          class="py-1 px-2 bg-zinc-100 text-gray-600 flex items-center gap-1 justify-center text-sm hover:shadow hover:bg-gray-200 border border-gray-300 shadow whitespace-nowrap"
          onclick={print}
        >
          <Printer class="size-4 text-gray-600" />
          Print Current Week</button
        >
      {/if}
    </div>
  </div>
  <div id="print-log" style="display: none;">
    <h2 class="text-2xl mt-4">{appStore.plan.name} Weekly Log</h2>
    <h3 class="text-lg mt-4">
      For the week of {monthsAbbr[dateStart.getMonth()]}
      {dateStart.getDate()}, {dateStart.getFullYear()}
    </h3>
    <hr />
    <div class="mt-12">
      {#if currentNote.notes && currentNote.notes != "<p></p>" && currentNote.notes != "null"}
        {@html currentNote.notes}
      {:else}
        <p class="italic text-base">No notes entered for this week.</p>
      {/if}
    </div>
  </div>
  <div class="relative" id="weekly-log">
    <div class="ml-4 bg-gray-50">
      {#if appStore.is_leadership && date_start == curr_date_start}
        <div class="text-left inline-block w-full">
          <div class="h-64 relative">
            {#if saving}
              <div
                transition:fade
                class="absolute top-1 right-2 bg-green-500 text-white text-xs py-1 px-2 rounded opacity-50"
              >
                Saving...
              </div>
            {:else}
              <div transition:fade class="absolute top-1 right-2">
                <button
                  class="px-3 py-1 text-xs border rounded-sm border-gray-300"
                  onclick={() => saveMeetingNotes(date_end)}>Save</button
                >
              </div>
            {/if}
            <Tiptap
              content={currentNoteText}
              onupdate={updateNotes}
              allowEmojis={true}
              allowGiphy={true}
              class="h-36"
              placeholder="Start typing..."
              buttons={[
                ["bold", "italic", "underline", "strike"],
                ["bulletList", "orderedList", "blockquote"],
                ["link", "unlink", "image"]
              ]}
            />
          </div>
        </div>
      {:else}
        <div
          class="py-4 px-8 h-64 overflow-y-scroll overflow-hidden ProseMirror"
        >
          {@html currentNote.notes && currentNote.notes != "<p></p>"
            ? currentNote.notes
            : "No notes for this week"}
        </div>
      {/if}
    </div>
  </div>
{:catch error}
  <div class="text-red-500">There was an error fetching the activities.</div>
{/await}

<style>
  @media print {
    #print-log {
      display: block;
    }
  }
</style>
